import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { Badge } from "../ui/badge";
import {
  PlayCircle,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  TrendingUp,
  RotateCcw,
  Database,
} from "lucide-react";
import {
  useHubSpotEmailMetricsApi,
  type HubSpotEmailMetricsBackfillResponse,
  type HubSpotEmailMetricsBackfillStatus,
  type HubSpotEmailMetricsState,
  type HubSpotEmailMetricsSyncResult,
  type HubSpotEmailMetricsResetResult,
} from "../../services/apiService";
import { useSharedPermissions } from "../../contexts/PermissionsContext";

const STATUS_COLORS = {
  Pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  Running: "bg-blue-100 text-blue-800 border-blue-200",
  Completed: "bg-green-100 text-green-800 border-green-200",
  Failed: "bg-red-100 text-red-800 border-red-200",
  Terminated: "bg-gray-100 text-gray-800 border-gray-200",
  Unknown: "bg-orange-100 text-orange-800 border-orange-200",
};

const STATUS_ICONS = {
  Pending: AlertCircle,
  Running: RefreshCw,
  Completed: CheckCircle,
  Failed: XCircle,
  Terminated: AlertCircle,
  Unknown: AlertCircle,
};

interface LocalBackfillStatus {
  response: HubSpotEmailMetricsBackfillResponse;
  status?: HubSpotEmailMetricsBackfillStatus;
}

export function HubSpotEmailMetricsBackfill() {
  const permissions = useSharedPermissions();
  const canManageHubSpot = permissions.canManageHubSpot || permissions.isAdmin;
  const emailMetricsApi = useHubSpotEmailMetricsApi();

  const [startDateUtc, setStartDateUtc] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 2);
    return date.toISOString().split("T")[0];
  });

  const [isStarting, setIsStarting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isTriggeringUpdate, setIsTriggeringUpdate] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const [state, setState] = useState<HubSpotEmailMetricsState | null>(null);
  const [lastBackfill, setLastBackfill] = useState<LocalBackfillStatus | null>(null);
  const [lastUpdateResult, setLastUpdateResult] = useState<HubSpotEmailMetricsSyncResult | null>(null);
  const [lastResetResult, setLastResetResult] = useState<HubSpotEmailMetricsResetResult | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const refreshState = async () => {
    if (!canManageHubSpot) {
      return;
    }

    setIsRefreshing(true);
    try {
      const currentState = await emailMetricsApi.getState();
      setState(currentState);

      const storedInstanceId = localStorage.getItem("hubspot-email-metrics-backfill-instance");
      if (storedInstanceId) {
        try {
          const status = await emailMetricsApi.getBackfillStatus(storedInstanceId);
          setLastBackfill((previous) => {
            if (previous?.response.instanceId === storedInstanceId) {
              return { ...previous, status };
            }

            return {
              response: {
                instanceId: storedInstanceId,
                startDateUtc: previous?.response.startDateUtc || state?.backfillStartUtc || new Date().toISOString(),
                statusQueryUri: "",
                timestamp: previous?.response.timestamp || new Date().toISOString(),
              },
              status,
            };
          });
        } catch {
          // Keep UI resilient when orchestration has been cleaned up
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load email metrics state");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (!canManageHubSpot) {
      return;
    }

    refreshState();
  }, [canManageHubSpot]);

  useEffect(() => {
    if (!lastBackfill?.status) {
      return;
    }

    const runtimeStatus = lastBackfill.status.runtimeStatus;
    if (runtimeStatus !== "Running" && runtimeStatus !== "Pending") {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const latestStatus = await emailMetricsApi.getBackfillStatus(lastBackfill.response.instanceId);
        setLastBackfill((previous) => {
          if (!previous) return previous;
          return { ...previous, status: latestStatus };
        });
      } catch {
        // Ignore polling errors
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [lastBackfill?.response.instanceId, lastBackfill?.status?.runtimeStatus]);

  const handleStartBackfill = async () => {
    if (!canManageHubSpot) {
      setError("You do not have permission to manage HubSpot email metrics");
      return;
    }

    setIsStarting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await emailMetricsApi.startBackfill({
        startDateUtc: startDateUtc ? new Date(startDateUtc).toISOString() : undefined,
      });

      localStorage.setItem("hubspot-email-metrics-backfill-instance", response.instanceId);

      const status = await emailMetricsApi.getBackfillStatus(response.instanceId);
      setLastBackfill({ response, status });
      setSuccess(`Email metrics backfill started. Instance ID: ${response.instanceId}`);

      await refreshState();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start email metrics backfill");
    } finally {
      setIsStarting(false);
    }
  };

  const handleTriggerUpdate = async () => {
    setIsTriggeringUpdate(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await emailMetricsApi.triggerUpdate();
      setLastUpdateResult(result);
      setSuccess(`Incremental update finished. Processed ${result.totalProcessed} campaign metrics.`);
      await refreshState();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to trigger update");
    } finally {
      setIsTriggeringUpdate(false);
    }
  };

  const handleReset = async () => {
    const confirmed = window.confirm("Are you sure you want to reset HubSpot email metrics data?");
    if (!confirmed) {
      return;
    }

    setIsResetting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await emailMetricsApi.resetMetrics();
      setLastResetResult(result);
      setSuccess(`Reset completed. Deleted ${result.deletedRows} rows.`);
      await refreshState();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset email metrics");
    } finally {
      setIsResetting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    const Icon = STATUS_ICONS[status as keyof typeof STATUS_ICONS] || AlertCircle;
    return <Icon className="h-4 w-4" />;
  };

  if (!canManageHubSpot) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 ml-0 lg:ml-64">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You do not have permission to access HubSpot email metrics.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-5xl px-4 py-6 ml-0 lg:ml-64 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">HubSpot Email Metrics</h1>
        <p className="text-muted-foreground">
          Backfill and retrieve HubSpot email campaign metrics via backend Azure Functions.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PlayCircle className="h-5 w-5" />
            Start Email Metrics Backfill
          </CardTitle>
          <CardDescription>
            Starts durable orchestration using HubSpotEmailMetricsFunctions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 max-w-sm">
            <Label htmlFor="emailMetricsStartDate">Start Date (UTC)</Label>
            <Input
              id="emailMetricsStartDate"
              type="date"
              value={startDateUtc}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setStartDateUtc(e.target.value)}
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            <CapabilityButton capability={P.HubspotEmailMetricsBackfillCreate} onClick={handleStartBackfill} disabled={isStarting}>
              {isStarting ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <PlayCircle className="mr-2 h-4 w-4" />
                  Start Backfill
                </>
              )}
            </CapabilityButton>

            <Button variant="outline" onClick={refreshState} disabled={isRefreshing}>
              {isRefreshing ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh State
                </>
              )}
            </Button>

            <CapabilityButton capability={P.HubspotEmailMetricsUpdate} variant="secondary" onClick={handleTriggerUpdate} disabled={isTriggeringUpdate}>
              {isTriggeringUpdate ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Running Update...
                </>
              ) : (
                <>
                  <TrendingUp className="mr-2 h-4 w-4" />
                  Trigger Incremental Update
                </>
              )}
            </CapabilityButton>

            <CapabilityButton capability={P.HubspotEmailMetricsReset} variant="destructive" onClick={handleReset} disabled={isResetting}>
              {isResetting ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset Metrics
                </>
              )}
            </CapabilityButton>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              This interface calls the Azure Functions endpoints under <code>/hubspot/email-metrics/*</code>.
            </AlertDescription>
          </Alert>

          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {lastBackfill && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Latest Backfill Run
            </CardTitle>
            <CardDescription>
              Instance {lastBackfill.response.instanceId}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {lastBackfill.status ? (
              <>
                <Badge className={STATUS_COLORS[lastBackfill.status.runtimeStatus as keyof typeof STATUS_COLORS] || STATUS_COLORS.Unknown}>
                  {getStatusIcon(lastBackfill.status.runtimeStatus)}
                  <span className="ml-1">{lastBackfill.status.runtimeStatus}</span>
                </Badge>

                <div className="text-sm text-muted-foreground space-y-1">
                  <div>Created: {new Date(lastBackfill.status.createdAt).toLocaleString()}</div>
                  <div>Updated: {new Date(lastBackfill.status.lastUpdatedAt).toLocaleString()}</div>
                  {lastBackfill.status.failureDetails && (
                    <div className="text-red-600">Failure: {JSON.stringify(lastBackfill.status.failureDetails)}</div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">No status available yet.</div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Current Sync State
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!state ? (
            <div className="text-sm text-muted-foreground">No state data loaded yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-medium">Backfill Running</div>
                <div className="text-muted-foreground">{state.isBackfillRunning ? "Yes" : "No"}</div>
              </div>
              <div>
                <div className="font-medium">Backfill Completed</div>
                <div className="text-muted-foreground">{state.isBackfillCompleted ? "Yes" : "No"}</div>
              </div>
              <div>
                <div className="font-medium">Last Successful Sync</div>
                <div className="text-muted-foreground">
                  {state.lastSuccessfulSyncUtc ? new Date(state.lastSuccessfulSyncUtc).toLocaleString() : "-"}
                </div>
              </div>
              <div>
                <div className="font-medium">State Updated At</div>
                <div className="text-muted-foreground">{new Date(state.updatedAtUtc).toLocaleString()}</div>
              </div>
              <div>
                <div className="font-medium">Backfill Start</div>
                <div className="text-muted-foreground">
                  {state.backfillStartUtc ? new Date(state.backfillStartUtc).toLocaleString() : "-"}
                </div>
              </div>
              <div>
                <div className="font-medium">Backfill End</div>
                <div className="text-muted-foreground">
                  {state.backfillEndUtc ? new Date(state.backfillEndUtc).toLocaleString() : "-"}
                </div>
              </div>
              {state.lastError && (
                <div className="md:col-span-2">
                  <div className="font-medium text-red-600">Last Error</div>
                  <div className="text-muted-foreground">{state.lastError}</div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {(lastUpdateResult || lastResetResult) && (
        <Card>
          <CardHeader>
            <CardTitle>Latest Manual Operations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {lastUpdateResult && (
              <div>
                <div className="font-medium">Last Update Trigger Result</div>
                <div className="text-muted-foreground">
                  Success: {lastUpdateResult.success ? "Yes" : "No"}, Processed: {lastUpdateResult.totalProcessed},
                  Inserted: {lastUpdateResult.inserted}, Updated: {lastUpdateResult.updated}
                </div>
              </div>
            )}
            {lastResetResult && (
              <div>
                <div className="font-medium">Last Reset Result</div>
                <div className="text-muted-foreground">
                  Deleted Rows: {lastResetResult.deletedRows}, At: {new Date(lastResetResult.resetAtUtc).toLocaleString()}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
